"use client";

import { useMemo } from "react";
import { StorageKeys } from "~/services/StorageKeys";
import { isQuizCompleted } from "~/services/questionnaire/checkIsElligible";
import { getStorageItem } from "~/lib/storage/Storage";
import useUserIsAuth from "./useIsAuth";
import { QUIZ_CATEGORIES } from "~/constants/questionnaire";
import { useQuizCompletionSimulation } from "~/lib/qa/QuizCompletionQaProvider";

/**
 * Checks whether a quiz with the given category has been completed.
 *
 * Uses two sources (fast → slow):
 * 1. **localStorage** — synchronous, available immediately (covers same-browser sessions).
 * 2. **Auth historyLogs** — requires auth to be loaded (covers cross-device sessions).
 *
 * @param category - The quiz category to check (e.g. `QUIZ_CATEGORIES.FACE_CREAM`).
 *
 * @returns
 * - `isCompleted` — `true` if either source confirms the quiz was completed.
 * - `isChecking`  — `true` while waiting for auth data AND localStorage has no record.
 *                    Once auth loads or localStorage confirms completion, becomes `false`.
 *
 * @example
 * ```tsx
 * // Inside Quiz component — redirect if completed
 * const { isCompleted } = useQuizCompleted(category);
 *
 * // On a button — skip quiz page if already completed
 * const { isCompleted, isChecking } = useQuizCompleted(QUIZ_CATEGORIES.FACE_CREAM);
 *
 * const handleClick = () => {
 *     if (isCompleted) {
 *         router.push("/pqp-upsell/anti-aging-face-cream");
 *     } else {
 *         router.push("/quiz/face-cream");
 *     }
 * };
 * ```
 */
export const useQuizCompleted = (category: string) => {
    const { user, isAuthUser, isLoaded } = useUserIsAuth();
    const isSimulatedCompleted = useQuizCompletionSimulation(category);

    const isLocalStorageCompleted = useMemo(() => {
        const quizCompletedKey = StorageKeys.quiz.completed(category);
        return getStorageItem(quizCompletedKey) === "true";
    }, [category]);

    const isAuthCompleted = useMemo(() => {
        if (!isLoaded || !isAuthUser || !user?.id) return false;

        if (category) {
            return isQuizCompleted(user.historyLogs, category);
        }

        return isQuizCompleted(user.historyLogs, QUIZ_CATEGORIES.OESTRA_CREAM) || isQuizCompleted(user.historyLogs, QUIZ_CATEGORIES.OESTRA_CREAM_REDESIGN);
    }, [isLoaded, isAuthUser, user?.id, user?.historyLogs, category]);

    const isCompleted = isSimulatedCompleted || isLocalStorageCompleted || isAuthCompleted;
    const isChecking = !isLoaded && !isSimulatedCompleted && !isLocalStorageCompleted;

    return { isCompleted, isChecking };
};

export default useQuizCompleted;
